import * as React from 'react';
import { useSelector } from 'react-redux';

import { useRouter } from 'next/router';

import type {
  SaasMigrationSetting,
  SaasPlan,
  SaasSubscription,
} from '@evenfinancial/finance-client';
import { AccountTierType } from '@evenfinancial/finance-client';

import { getPlans } from '@/pages/self-service/account/tabs/subscription/states/free-subscription';
import {
  selectLastPurchasedSubscription,
  selectSaasPlansForAccountSubscription,
} from '@/resources/saas/selects';
import { getPreviousTiers } from '@/resources/self-service/payment/util-methods';
import {
  selectIsFirstRolloutOnboarding,
  selectIsImplementationFeeWaived,
} from '@/resources/self-service/selects';
import { isPermanentTrial } from '@/resources/supply-migration/helpers';
import { selectSaasMigrationSettings } from '@/resources/supply-migration/selects';

import type { SubscriptionPlanType } from './SubscriptionPlanCard/types';
import { SubscriptionPlansUI } from './SubscriptionPlansUI';

export type SubscriptionPlansProps = {
  currentPlan?: SaasPlan;
  currentSubscription?: SaasSubscription;
};

const getAvailablePlans = (
  planTypes: SubscriptionPlanType[],
  currentPlan?: SaasPlan,
  currentSubscription?: SaasSubscription,
  supplyMigration?: SaasMigrationSetting
) => {
  const tiersToRemove: AccountTierType[] = [];

  const showTier = (
    currentTier: AccountTierType,
    previousTier: AccountTierType | undefined
  ) =>
    currentPlan?.tier === previousTier ||
    (currentPlan?.tier === currentTier && currentSubscription?.deletedAt);

  const isPermanent = isPermanentTrial(supplyMigration!);

  const showStarter = showTier(AccountTierType.Starter, undefined);

  const showGrowth =
    showStarter || showTier(AccountTierType.Growth, AccountTierType.Starter);

  const showScale =
    showGrowth || showTier(AccountTierType.Scale, AccountTierType.Growth);

  if (!showStarter) {
    tiersToRemove.push(AccountTierType.Starter);
  }

  if (!showGrowth) {
    tiersToRemove.push(AccountTierType.Growth);
  }

  if (!showScale) {
    tiersToRemove.push(AccountTierType.Scale);
  }

  if (supplyMigration?.tier) {
    tiersToRemove.push(...getPreviousTiers(supplyMigration.tier));

    if (isPermanent) {
      tiersToRemove.push(supplyMigration.tier);
    }
  }

  return planTypes.map((planType) => {
    if (!tiersToRemove.includes(planType.id)) {
      return planType;
    }

    const actionButtonTitle =
      planType.id === currentPlan?.tier ? 'Current plan' : 'Unavailable';

    const isDisabled = planType.id !== currentPlan?.tier;

    return {
      ...planType,
      actionButtonTitle,
      cardClassName: `${planType.cardClassName} unavailable`,
      isDisabled,
      onActionButtonClick: undefined,
    };
  });
};

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  currentPlan,
  currentSubscription,
}) => {
  const firstRolloutOnboarding = useSelector(selectIsFirstRolloutOnboarding);
  const implementationFeeWaived = useSelector(selectIsImplementationFeeWaived);
  const lastPurchasedSubscription = useSelector(
    selectLastPurchasedSubscription
  );
  const saasPlans = useSelector(selectSaasPlansForAccountSubscription);
  const supplyMigration = useSelector(selectSaasMigrationSettings);

  const {
    query: { id: integrationId, submit },
  } = useRouter();
  const isSubmitForReview: boolean = Boolean(submit);
  const findMigrationPlan =
    !currentSubscription &&
    Boolean(supplyMigration) &&
    !isPermanentTrial(supplyMigration);
  const plans = getPlans(
    saasPlans,
    implementationFeeWaived,
    firstRolloutOnboarding,
    findMigrationPlan,
    supplyMigration,
    currentSubscription,
    currentPlan,
    lastPurchasedSubscription,
    integrationId as string,
    isSubmitForReview
  );

  const plansAvailable = getAvailablePlans(
    plans,
    currentPlan,
    currentSubscription,
    supplyMigration
  );

  return (
    <SubscriptionPlansUI
      currentPlan={currentPlan}
      implementationFeeWaived={implementationFeeWaived}
      isSubmitForReview={isSubmitForReview}
      plans={plansAvailable}
    />
  );
};

export default SubscriptionPlans;
